import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from "react-router-dom";
import Journal from "./Journal";
import History from "./History";
import QuestionHistoryAnswers from "./QuestionHistoryAnswers"; // patrz sekcja 2 niżej!
import Dashboard from "./Dashboard";
import Goals from "./Goals";
import Login from "./Login";
import Ranking from "./Ranking";
import Logo from "./Logo_cleardone.png";
import {
  Book,
  BookOpen,
  ListChecks,
  Clock,
  Moon,
  Sun,
  BarChart,
  Target,
  Settings,
  LogOut,
  Trophy
} from "lucide-react";
import { auth } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";

function App() {
  const [darkMode, setDarkMode] = useState(false);
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [darkMode]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoadingAuth(false);
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Błąd przy wylogowaniu:", error);
    }
  };

  if (loadingAuth) {
    return (
      <div className="flex items-center justify-center h-screen">
        Ładowanie...
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 font-sans transition-colors duration-300 dark:from-gray-800 dark:to-gray-700">
        <div className="max-w-4xl mx-auto p-6">
          {/* Nagłówek */}
          <header className="bg-white/60 dark:bg-gray-900/60 backdrop-blur-md p-4 rounded-xl shadow-md mb-6 sticky top-0 z-50">
            {/* Desktop */}
            <div className="hidden sm:flex items-center justify-between">
              <div className="flex items-center">
                <img src={Logo} alt="Logo" className="mr-2 w-10 h-10" />
                <h1 className="text-2xl md:text-3xl font-bold" style={{ color: '#004225' }}>
                  AI Journaly
                </h1>
              </div>
              <div className="flex items-center space-x-4">
                <NavLink
                  to="/"
                  end
                  className={({ isActive }) =>
                    `flex items-center transition px-3 py-2 rounded-md ${
                      isActive
                        ? "bg-emerald-200 text-emerald-900"
                        : "text-emerald-700 hover:bg-emerald-100"
                    }`
                  }
                >
                  <Book size={20} />
                  <span className="ml-1">Dziennik</span>
                </NavLink>
                <div className="relative group">
                  <button className="flex items-center px-3 py-2 rounded-md text-emerald-700 hover:bg-emerald-100 transition">
                    <Clock size={20} />
                    <span className="ml-1">Historia</span>
                  </button>
                  {/* Dropdown dla historii */}
                  <div className="absolute left-0 mt-2 w-60 bg-white dark:bg-gray-900 rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-40 py-2">
                    <NavLink
                      to="/history/overview"
                      className={({ isActive }) =>
                        `flex items-center gap-2 px-4 py-2 text-base font-semibold rounded-md transition ${
                          isActive
                            ? "bg-emerald-100 text-emerald-900"
                            : "text-emerald-700 hover:bg-emerald-50"
                        }`
                      }
                    >
                      <BookOpen size={18} />
                      Przegląd wpisów
                    </NavLink>
                    <NavLink
                      to="/history/answers"
                      className={({ isActive }) =>
                        `flex items-center gap-2 px-4 py-2 text-base font-semibold rounded-md transition ${
                          isActive
                            ? "bg-green-100 text-green-900"
                            : "text-green-700 hover:bg-green-50"
                        }`
                      }
                    >
                      <ListChecks size={18} />
                      Historia odpowiedzi
                    </NavLink>
                  </div>
                </div>
                <NavLink
                  to="/dashboard"
                  className={({ isActive }) =>
                    `flex items-center transition px-3 py-2 rounded-md ${
                      isActive
                        ? "bg-blue-200 text-blue-900"
                        : "text-blue-700 hover:bg-blue-100"
                    }`
                  }
                >
                  <BarChart size={20} />
                  <span className="ml-1">Dashboard</span>
                </NavLink>
                {/* Dropdown: Konfiguracja */}
                <div className="relative group">
                  <button className="flex items-center transition px-3 py-2 rounded-md text-indigo-700 hover:bg-indigo-100">
                    <Settings size={20} className="mr-1" />
                    Konfiguracja
                  </button>
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-900 rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-40 py-2">
                    <NavLink
                      to="/goals"
                      className={({ isActive }) =>
                        `flex items-center gap-2 px-4 py-2 text-base font-semibold rounded-md transition ${
                          isActive
                            ? "bg-purple-100 text-purple-700"
                            : "text-purple-700 hover:bg-purple-50"
                        }`
                      }
                    >
                      <Target size={18} />
                      Cele
                    </NavLink>
                    <NavLink
                      to="/ranking"
                      className={({ isActive }) =>
                        `flex items-center gap-2 px-4 py-2 text-base font-semibold rounded-md transition ${
                          isActive
                            ? "bg-yellow-100 text-yellow-800"
                            : "text-yellow-700 hover:bg-yellow-50"
                        }`
                      }
                      title="Ranking realizacji celów"
                    >
                      <Trophy size={18} className="text-yellow-500" />
                      Ranking
                    </NavLink>
                  </div>
                </div>
                <button
                  onClick={() => setDarkMode(!darkMode)}
                  className="p-2 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-500 transition"
                  title="Przełącz tryb ciemny/jasny"
                >
                  {darkMode ? <Sun size={20} /> : <Moon size={20} />}
                </button>
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-full bg-red-100 dark:bg-red-700 text-red-800 dark:text-red-200 hover:bg-red-200 dark:hover:bg-red-600 transition"
                  title="Wyloguj się"
                >
                  <LogOut size={20} />
                </button>
              </div>
            </div>

            {/* Mobile */}
            <div className="sm:hidden flex items-center justify-end">
              <div className="flex items-center space-x-4">
                <NavLink to="/" className="flex items-center" title="Dziennik">
                  <Book className="text-emerald-700" size={20} />
                </NavLink>
                <div className="relative">
                  <button
                    onClick={() => setMobileDropdownOpen(mobileDropdownOpen === "history" ? false : "history")}
                    className="flex items-center"
                    title="Historia"
                  >
                    <Clock className="text-emerald-700" size={20} />
                  </button>
                  {mobileDropdownOpen === "history" && (
                    <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-gray-900 rounded-md shadow-lg z-40 py-2">
                      <NavLink
                        to="/history/overview"
                        className={({ isActive }) =>
                          `flex items-center gap-2 px-4 py-2 text-base font-semibold rounded-md transition ${
                            isActive
                              ? "bg-emerald-100 text-emerald-900"
                              : "text-emerald-700 hover:bg-emerald-50"
                          }`
                        }
                        onClick={() => setMobileDropdownOpen(false)}
                      >
                        <BookOpen size={18} />
                        Przegląd wpisów
                      </NavLink>
                      <NavLink
                        to="/history/answers"
                        className={({ isActive }) =>
                          `flex items-center gap-2 px-4 py-2 text-base font-semibold rounded-md transition ${
                            isActive
                              ? "bg-green-100 text-green-900"
                              : "text-green-700 hover:bg-green-50"
                          }`
                        }
                        onClick={() => setMobileDropdownOpen(false)}
                      >
                        <ListChecks size={18} />
                        Historia odpowiedzi
                      </NavLink>
                    </div>
                  )}
                </div>
                <NavLink to="/dashboard" className="flex items-center" title="Dashboard">
                  <BarChart className="text-blue-700" size={20} />
                </NavLink>
                <div className="relative">
                  <button
                    onClick={() => setMobileDropdownOpen(mobileDropdownOpen === "config" ? false : "config")}
                    className="flex items-center"
                    title="Konfiguracja"
                  >
                    <Settings size={20} className="text-indigo-700" />
                  </button>
                  {mobileDropdownOpen === "config" && (
                    <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-gray-900 rounded-md shadow-lg z-40 py-2">
                      <NavLink
                        to="/goals"
                        className={({ isActive }) =>
                          `flex items-center gap-2 px-4 py-2 text-base font-semibold rounded-md transition ${
                            isActive
                              ? "bg-purple-100 text-purple-700"
                              : "text-purple-700 hover:bg-purple-50"
                          }`
                        }
                        onClick={() => setMobileDropdownOpen(false)}
                      >
                        <Target size={18} />
                        Cele
                      </NavLink>
                      <NavLink
                        to="/ranking"
                        className={({ isActive }) =>
                          `flex items-center gap-2 px-4 py-2 text-base font-semibold rounded-md transition ${
                            isActive
                              ? "bg-yellow-100 text-yellow-800"
                              : "text-yellow-700 hover:bg-yellow-50"
                          }`
                        }
                        onClick={() => setMobileDropdownOpen(false)}
                        title="Ranking realizacji celów"
                      >
                        <Trophy size={18} className="text-yellow-500" />
                        Ranking
                      </NavLink>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setDarkMode(!darkMode)}
                  className="p-2 transition"
                  title="Przełącz tryb ciemny/jasny"
                >
                  {darkMode ? <Sun size={20} /> : <Moon size={20} />}
                </button>
                <button
                  onClick={handleLogout}
                  className="p-2 transition"
                  title="Wyloguj się"
                >
                  <LogOut className="text-red-700" size={20} />
                </button>
              </div>
            </div>
          </header>

          {/* Główna zawartość */}
          <main className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-md rounded-xl shadow-lg transition-colors duration-300">
            <Routes>
              <Route path="/" element={<Journal />} />
              <Route path="/history/overview" element={<History />} />
              <Route path="/history/answers" element={<QuestionHistoryAnswers />} />
              <Route path="/ranking" element={<Ranking />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/goals" element={<Goals />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </main>

          {/* Stopka */}
          <footer className="text-center mt-6 text-emerald-700 dark:text-green-200 bg-white/60 dark:bg-gray-900/60 backdrop-blur-md p-4 rounded-xl shadow-md">
            © 2025 AI Journaling - Dziennik Progresu
          </footer>
        </div>
      </div>
    </Router>
  );
}

export default App;
